import React from "react";
import { PaymentPendingCard } from "../cards/PaymentPendingCard";
import { MessageLayout } from "../layout/MessageLayout";
import { useChatActions } from "../../context/ChatActionContext";
import type { ChatMessageProps } from "../types";
import type { PaymentPendingMessageResponseDto } from "@semo/api-contracts";
import { PaymentPendingDataDtoStatus } from "@semo/api-contracts";
import type { LocalMessageState } from "../../types/message";

interface PaymentPendingData {
  status?: PaymentPendingDataDtoStatus;
  amount?: number;
  bankAccount?: string;
  moid?: string;
  expiresAt?: string;
  rejectionReason?: string;
}

interface PaymentPendingMessageProps extends Omit<ChatMessageProps, "message"> {
  message: (Omit<PaymentPendingMessageResponseDto, "paymentPendingData"> & {
    paymentPendingData?: PaymentPendingData;
  }) &
    LocalMessageState;
  sender: "me" | "other";
  time: string;
}

export const PaymentPendingMessage: React.FC<PaymentPendingMessageProps> = ({
  message,
  role,
  sender,
  partyName,
  time,
  showAvatar = true,
  showName = true,
}) => {
  const { isRead, createdAt, isPending, isError } = message;
  const { onRefresh, onRetry } = useChatActions();

  if (!message.paymentPendingData) return null;

  // ✅ Hide rejected message for Vendor (Supplier)
  if (role === "supplier" && message.paymentPendingData.status === "REJECTED") {
    return null;
  }

  const amount = message.paymentPendingData.amount ?? 0;
  const displayAmount = `${amount.toLocaleString("ko-KR")}원`;

  // Compute expired state from expiresAt
  const isExpired = message.paymentPendingData.expiresAt
    ? new Date(message.paymentPendingData.expiresAt) < new Date()
    : false;

  return (
    <MessageLayout
      sender={sender}
      partyName={partyName}
      time={time}
      isRead={isRead}
      isPending={isPending}
      isError={isError}
      showAvatar={showAvatar}
      showName={showName}
      variant="card"
      onRetry={() => onRetry?.(message.id)}
    >
      <PaymentPendingCard
        message={{ createdAt }}
        amount={displayAmount}
        sender={sender}
        moid={message.paymentPendingData.moid ?? ""}
        bankAccount={message.paymentPendingData.bankAccount}
        expiresAt={message.paymentPendingData.expiresAt}
        onExpired={() => onRefresh?.()}
        isExpired={isExpired}
        status={message.paymentPendingData.status}
        rejectionReason={message.paymentPendingData.rejectionReason}
      />
    </MessageLayout>
  );
};
